import React from 'react';
import { motion } from 'framer-motion';
import { Map, Target, Zap, ArrowRight, Activity, Users, AlertTriangle } from 'lucide-react';
export function Slide3Bottleneck() {
  const stages = [{
    name: 'Pre-Congress',
    icon: Users,
    bottleneck: 'small'
  }, {
    name: 'Vicinity',
    icon: Map,
    bottleneck: 'small'
  }, {
    name: 'Venue',
    icon: Target,
    bottleneck: 'small'
  }, {
    name: 'Aisle',
    icon: Zap,
    bottleneck: 'large'
  }, {
    name: 'Booth',
    icon: Activity,
    bottleneck: 'small'
  }, {
    name: 'Post-Congress',
    icon: ArrowRight,
    bottleneck: 'small'
  }];
  return <div className="h-full w-full bg-[#F8F9FA] text-[#2D3748] flex flex-col p-12 md:px-24 md:pt-20 md:pb-28 relative overflow-hidden" data-id="element-125">
      <header className="mb-16" data-id="element-126">
        <motion.h2 className="text-4xl md:text-5xl font-bold text-[#1A202C] mb-4" initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.6
      }} data-id="element-127">
          Do not activate everything.{' '}
          <span className="text-[#E60012]" data-id="element-128">
            Start where the bottleneck is biggest.
          </span>
        </motion.h2>
        <motion.p className="text-xl text-[#4A5568] font-light" initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} data-id="element-129">
          You do not have to activate the full journey. Optimize the stages
          where the bottleneck is worst and the low-hanging fruit is.
        </motion.p>
      </header>

      <main className="flex-1 flex flex-col justify-center" data-id="element-130">
        <motion.div className="relative w-full mb-20" initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }} data-id="element-131">
          <div className="absolute top-1/2 left-0 w-full h-1 bg-gray-200 -translate-y-1/2 rounded-full" data-id="element-132" />

          <div className="relative flex justify-between items-center w-full" data-id="element-133">
            {stages.map((stage, index) => {
            const Icon = stage.icon;
            const isLargeBottleneck = stage.bottleneck === 'large';
            return <motion.div key={stage.name} className="flex flex-col items-center relative" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.4 + index * 0.1
            }} data-id="element-134">
                  {/* Bottleneck Indicator */}
                  {isLargeBottleneck ? <motion.div className="absolute -top-16 flex flex-col items-center" initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 1.2,
                type: 'spring'
              }} data-id="element-135">
                      <div className="bg-[#E60012] text-white px-3 py-1 rounded-md text-xs font-bold shadow-lg flex items-center gap-1 mb-2" data-id="element-136">
                        <AlertTriangle className="w-3 h-3" data-id="element-137" />
                        PRIMARY BOTTLENECK?
                      </div>
                      <div className="w-0.5 h-6 bg-[#E60012]" data-id="element-138" />
                    </motion.div> : <motion.div className="absolute -top-6 w-3 h-3 rounded-full bg-[#E60012]/30" initial={{
                opacity: 0,
                scale: 0
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                delay: 1 + index * 0.1
              }} data-id="element-139" />}

                  <div className={`w-16 h-16 rounded-full bg-white shadow-md border-2 flex items-center justify-center mb-4 relative z-10 transition-colors duration-300 ${isLargeBottleneck ? 'border-[#E60012] ring-4 ring-[#E60012]/20' : 'border-gray-200'}`} data-id="element-140">
                    <Icon className={`w-6 h-6 ${isLargeBottleneck ? 'text-[#E60012]' : 'text-gray-400'}`} data-id="element-141" />
                  </div>
                  <span className={`text-sm font-semibold ${isLargeBottleneck ? 'text-[#E60012]' : 'text-[#4A5568]'}`} data-id="element-142">
                    {stage.name}
                  </span>
                </motion.div>;
          })}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-143">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.8
        }} data-id="element-144">
            <p className="text-[#4A5568] leading-relaxed mb-6" data-id="element-145">
              For some campaigns, the bottleneck is low awareness before the
              congress. For others, it is poor aisle-to-booth conversion. For
              others, it is weak preference capture inside the booth. For
              others, it is post-congress follow-up without useful data.
            </p>
            <p className="text-[#1A202C] font-semibold text-lg" data-id="element-146">
              Snakker helps Takeda pinpoint where to optimize first.
            </p>
          </motion.div>

          <motion.div className="bg-white p-8 rounded-xl shadow-sm border-l-4 border-[#E60012] flex items-center" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 1
        }} data-id="element-147">
            <p className="text-xl font-medium text-[#2D3748]" data-id="element-148">
              This approach is already proven by other pharma players.
              <br data-id="element-149" />
              <span className="text-[#E60012]" data-id="element-150">
                Start where it matters most — not everywhere at once.
              </span>
            </p>
          </motion.div>
        </div>
      </main>
    </div>;
}